import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { OrgIndependenceActions } from './org-independence.actions';
import { OrganizationalIndependenceService } from '../../service/organizational-independence.service';


@Injectable()
export class OrgIndependenceEffects {

  loadOrganizationalIndependence$ = createEffect(
    (organizationalIndependenceService: OrganizationalIndependenceService = inject(OrganizationalIndependenceService)) => {
      return this.actions$.pipe(
        ofType(OrgIndependenceActions.loadDashboard),
        switchMap(({ parameters, campaignId }) =>
          organizationalIndependenceService.getOrganizationalAttestation(campaignId, parameters).pipe(
            map((dashboard) =>
              OrgIndependenceActions.loadDashboardSuccess({
                dashboard
              })
            ),
            catchError((error) => {
              return of(OrgIndependenceActions.loadDashboardFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );


  loadAllOrganizationalIndependence$ = createEffect(
    (organizationalIndependenceService: OrganizationalIndependenceService = inject(OrganizationalIndependenceService)) => {
      return this.actions$.pipe(
        ofType(OrgIndependenceActions.loadAllDashboard),
        switchMap(({ parameters, campaignId }) =>
          organizationalIndependenceService.getOrganizationalAttestation(campaignId, parameters).pipe(
            map((dashboard) =>
              OrgIndependenceActions.loadAllDashboardSuccess({
                allDashboard: dashboard
              })
            ),
            catchError((error) => {
              return of(OrgIndependenceActions.loadAllDashboardFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  loadCampaigns$ = createEffect(
    (organizationalIndependenceService: OrganizationalIndependenceService = inject(OrganizationalIndependenceService)) => {
      return this.actions$.pipe(
        ofType(OrgIndependenceActions.loadCampaigns),
        switchMap(() =>
          organizationalIndependenceService.getCampaigns().pipe(
            map((campaigns) =>
              OrgIndependenceActions.loadCampaignsSuccess({
                campaigns
              })
            ),
            catchError((error) => {
              return of(OrgIndependenceActions.loadCampaignsFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  createOrUpdateCampaign$ = createEffect(
    (organizationalIndependenceService: OrganizationalIndependenceService = inject(OrganizationalIndependenceService)) => {
      return this.actions$.pipe(
        ofType(OrgIndependenceActions.createOrUpdateCampaign),
        switchMap(({ id, year }) =>
          organizationalIndependenceService.createOrUpdateOrganizationalCampaignGQL(id, year).pipe(
            map((response) =>
              OrgIndependenceActions.createOrUpdateCampaignSuccess({
                campaign: response
              })
            ),
            catchError((error) => {
              return of(OrgIndependenceActions.createOrUpdateCampaignFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );


  closeCampaign$ = createEffect(
    (organizationalIndependenceService: OrganizationalIndependenceService = inject(OrganizationalIndependenceService)) => {
      return this.actions$.pipe(
        ofType(OrgIndependenceActions.closeCampaign),
        switchMap(({ id }) =>
          organizationalIndependenceService.closeAdherenceCampaign(id).pipe(
            map((response) =>
              OrgIndependenceActions.closeCampaignSuccess({
                campaign: response
              })
            ),
            catchError((error) => {
              return of(OrgIndependenceActions.closeCampaignFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  loadTemplate$ = createEffect(
    (organizationalIndependenceService: OrganizationalIndependenceService = inject(OrganizationalIndependenceService)) => {
      return this.actions$.pipe(
        ofType(OrgIndependenceActions.loadTemplate),
        switchMap(({ campaignId }) =>
          organizationalIndependenceService.getAttestationTemplate(campaignId).pipe(
            map((template) =>
              OrgIndependenceActions.loadTemplateSuccess({
                template
              })
            ),
            catchError((error) => {
              return of(OrgIndependenceActions.loadTemplateFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  createAndSignAttestation$ = createEffect(
    (organizationalIndependenceService: OrganizationalIndependenceService = inject(OrganizationalIndependenceService)) => {
      return this.actions$.pipe(
        ofType(OrgIndependenceActions.createAndSignAttestation),
        switchMap(({ legalEntityTrackingIds }) =>
          organizationalIndependenceService.createAndSignAttestation(legalEntityTrackingIds).pipe(
            map((attestation) =>
              OrgIndependenceActions.createAndSignAttestationSuccess({
                attestation
              })
            ),
            catchError((error) => {
              return of(OrgIndependenceActions.createAndSignAttestationFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  updateNotes$ = createEffect(
    (organizationalIndependenceService: OrganizationalIndependenceService = inject(OrganizationalIndependenceService)) => {
      return this.actions$.pipe(
        ofType(OrgIndependenceActions.updateAttestationNotes),
        switchMap(({ notes, legalEntityTrackingId, campaignId, parameters }) =>
          organizationalIndependenceService.updateNote(notes, legalEntityTrackingId, campaignId).pipe(
            mergeMap((attestation) => [
              OrgIndependenceActions.updateAttestationNotesSuccess({ attestation }),
              OrgIndependenceActions.loadAllDashboard({ campaignId, parameters }) // <-- seconda azione
            ]),
            catchError((error) =>
              of(OrgIndependenceActions.updateAttestationNotesFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  saveTemplate$ = createEffect(
    (organizationalIndependenceService: OrganizationalIndependenceService = inject(OrganizationalIndependenceService)) => {
      return this.actions$.pipe(
        ofType(OrgIndependenceActions.saveTemplate),
        switchMap(({ template }) =>
          organizationalIndependenceService.saveTemplate(template).pipe(
            map((template) =>
              OrgIndependenceActions.saveTemplateSuccess({
                template
              })
            ),
            catchError((error) => {
              return of(OrgIndependenceActions.saveTemplateFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  exportTemplate$ = createEffect(
    (organizationalIndependenceService: OrganizationalIndependenceService = inject(OrganizationalIndependenceService)) => {
      return this.actions$.pipe(
        ofType(OrgIndependenceActions.exportAttestationTemplate),
        mergeMap(({ campaignId, timeZone, parameters }) =>
          organizationalIndependenceService.exportAttestationTemplate(campaignId, timeZone, parameters).pipe(
            map((file) => {
              if (file?.data && file.data.length > 0) {
                const f = file.data[0];
                const byteChars = atob(f!.content);
                const byteNumbers = new Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) {
                  byteNumbers[i] = byteChars.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const link = window.document.createElement("a");
                link.href = window.URL.createObjectURL(blob);
                link.download = f!.filename ?? "attestation_template.xlsx";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
              }
              return OrgIndependenceActions.exportAttestationTemplateSuccess({
                file
              });
            }),
            catchError((error) => of(OrgIndependenceActions.exportAttestationTemplateFailure({ error })))
          )
        )
      )
    }
  );

  async downloadFile(response: string, fileName: string | undefined) {
    const typeFormat = {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
    const link = window.document.createElement("a");
    const blob = this.base64ToBlob(response, typeFormat.type);
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName ?? "attestation_template";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }


  constructor(private actions$: Actions) { }
}
